/**
 * The model for the LSCloud /Account/Customer/:id REST endpoint
 *
 * Copyright 2016 LightSpeed Retail. All rights reserved.
 * Created by kevinford on 2016-03-29.
 */
import RetailModel from 'app/src/data/models/RetailModel';
import URLHelper from 'app/src/data/models/utils/URLHelper';
import Format from 'app/src/data/Format';
import _ from 'underscore';

export default class Customer extends RetailModel {
  // Server uses the customerID field as the model's unique identifier. This
  // informs the Backbone.Model of this fact.
  get idAttribute() {
    return 'customerID';
  }

  get defaults() {
    return {
      firstName: '',
      lastName: '',
      dob: '',
      archived: false,
      title: '',
      company: '',
      createTime: '',
      timeStamp: '',
      creditAccountID: 0,
      customerTypeID: 0,
      discountID: 0,
      taxCategoryID: 0,
    };
  }

  get retailResponseName() {
    return 'Customer';
  }

  /**
   * Return the url for the Customer endpoint
   */
  url() {
    let URL = URLHelper.getAccountURL('/Customer');
    if (!this.isNew()) {
      URL += `/${this.id}`;
    }
    return `${URL}.json`;
  }

  /**
   * Overridden to parse out the superfluous json that the LSCloud returns us
   * @param response - the json object returned from the fetch call before this object has initialized from it
   * @returns {*} the json object that this object will initialize itself from
   */
  parse(response) {
    let parsedResponse = response.Customer || response;

    if (parsedResponse.Contact) {
      if (parsedResponse.Contact.Phones) {
        if (
          _.isObject(parsedResponse.Contact.Phones.ContactPhone) &&
          !_.isArray(parsedResponse.Contact.Phones.ContactPhone) &&
          !_.isEmpty(parsedResponse.Contact.Phones.ContactPhone)
        ) {
          parsedResponse.Contact.Phones.ContactPhone = [parsedResponse.Contact.Phones.ContactPhone];
        }
      }
      if (parsedResponse.Contact.Emails) {
        if (
          _.isObject(parsedResponse.Contact.Emails.ContactEmail) &&
          !_.isArray(parsedResponse.Contact.Emails.ContactEmail) &&
          !_.isEmpty(parsedResponse.Contact.Emails.ContactEmail)
        ) {
          parsedResponse.Contact.Emails.ContactEmail = [parsedResponse.Contact.Emails.ContactEmail];
        }
      }
    }

    // the Cloud API has a tendency to send all values as strings so let's make sure they're of the proper type
    parsedResponse.customerID = Format.parseInt(parsedResponse.customerID);
    parsedResponse.archived = Format.parseBoolean(parsedResponse.archived);
    parsedResponse.creditAccountID = Format.parseInt(parsedResponse.creditAccountID);
    parsedResponse.customerTypeID = Format.parseInt(parsedResponse.customerTypeID);
    parsedResponse.discountID = Format.parseInt(parsedResponse.discountID);
    parsedResponse.taxCategoryID = Format.parseInt(parsedResponse.taxCategoryID);

    return parsedResponse;
  }

  /**
   * Return the first and last name separated by a space
   *
   * @returns {string}
   */
  get fullName() {
    return `${this.get('firstName')} ${this.get('lastName')}`.trim();
  }

  /**
   * Retrieves the first phone number found for the customer
   *
   * @returns {string}     A phone number
   */
  get primaryContactNumber() {
    let phoneNumber = '';
    if (this.has('Contact') && this.get('Contact').Phones) {
      let phones = this.get('Contact').Phones;
      if (phones.ContactPhone) {
        phoneNumber = _.isArray(phones.ContactPhone)
          ? phones.ContactPhone[0].number
          : phones.ContactPhone.number;
      }
    }
    return phoneNumber;
  }

  /**
   * Retrieves the outstanding credit account balance
   *
   * @returns {Decimal}   The outstanding credit account balance
   */
  get creditAccountBalance() {
    let balance = Format.parseDecimal(0);

    if (this.has('CreditAccount')) {
      balance = Format.parseDecimal(this.get('CreditAccount').balance, 0).negated();
    }

    return balance;
  }

  /**
   * Retrieves the open orders for a customer
   *
   * @returns {*}
   */
  getOpenOrders() {
    let deferred = $.Deferred();

    if (!this.isNew()) {
      $.ajax(`${URLHelper.getAccountURL('/Customer')}/${this.id}/OpenOrders.json`, {
        type: 'GET',
        contentType: 'application/json',
        dataType: 'json',
      })
        .done(response => {
          if (response.OpenOrders) {
            deferred.resolve(response.OpenOrders);
          } else {
            deferred.reject();
          }
        })
        .fail(() => {
          deferred.reject();
        });
    } else {
      console.error('Cannot retrieve the open orders for an unknown customer.');
      deferred.reject();
    }

    return deferred.promise();
  }
}
